import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { HttpEventType, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { roleNamesOf } from '../auth/menu-access';
import { NotificationService } from '../notification/notification.service';
import { ConfirmDialogService } from '../confirm-dialog/confirm-dialog.service';
import {
  ChatAttachment,
  ChatConversation,
  ChatMember,
  ChatMessage,
  ChatReadState,
  ChatRefCard,
  ChatService,
  ChatStreamEvent,
} from '../stokker/api/chat.service';
import { avatarColor, clock, dayLabel, fileExt, fileSize, firstName, fullTime, initials, isEmojiOnly, lastSeenLabel, linkify } from './chat-utils';
import { isModelFile } from './chat-model-viewer.component';

/** Küldés alatt álló / sikertelen saját üzenet (helyi másolat). */
interface LocalState {
  key: string;
  status: 'sending' | 'failed';
  progress: number;
  files: File[];
  previews: (string | null)[];
  refs: ChatRefCard[];
  replyToId: number | null;
}

type ViewMessage = ChatMessage & { local?: LocalState };

interface PendingFile {
  file: File;
  preview: string | null;
}

interface Row {
  kind: 'day' | 'time' | 'msg';
  key: string;
  label?: string;
  msg?: ViewMessage;
  mine?: boolean;
  first?: boolean;
  last?: boolean;
  showName?: boolean;
  html?: string;
  emojiOnly?: boolean;
  images?: ChatAttachment[];
  files?: ChatAttachment[];
  readers?: ChatMember[];
  /** saját utolsó üzenet alatt: „Elküldve", ha még senki nem látta */
  sent?: boolean;
}

const GROUP_GAP_MS = 5 * 60 * 1000;
const TIME_GAP_MS = 30 * 60 * 1000;
const TYPING_SHOW_MS = 5000;
const TYPING_SEND_MS = 3000;
const LONG_PRESS_MS = 420;
const IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILES = 10;
const MAX_FILE_BYTES = 25 * 1024 * 1024;

@Component({
  selector: 'app-chat-thread',
  templateUrl: './chat-thread.component.html',
  styleUrls: ['./chat-thread.component.scss'],
})
export class ChatThreadComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() conversation!: ChatConversation;
  @Input() members: ChatMember[] = [];
  @Input() me = 0;
  @Output() back = new EventEmitter<void>();
  @Output() mutedChange = new EventEmitter<boolean>();

  @ViewChild('scroller') scroller?: ElementRef<HTMLDivElement>;
  @ViewChild('input') input?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  messages: ViewMessage[] = [];
  rows: Row[] = [];
  reads = new Map<number, ChatReadState>();
  hasMore = false;
  loading = true;
  loadingOlder = false;
  loadError = false;
  online = new Set<number>();
  typingUsers: number[] = [];
  newBelow = 0;
  atBottom = true;

  // üzenetíró
  text = '';
  pendingFiles: PendingFile[] = [];
  pendingRefs: ChatRefCard[] = [];
  replyTo: ViewMessage | null = null;
  editing: ViewMessage | null = null;
  attachMenu = false;
  refPicker = false;
  dragOver = false;

  // menük, nézetek
  pickerFor: number | null = null;
  moreFor: number | null = null;
  menuDown = false;
  sheet: ViewMessage | null = null;
  selectedTimeFor: number | null = null;
  info: {
    msg: ViewMessage;
    seen: { member: ChatMember; seenAt: string | null }[] | null;
    // egyszer számolt listák (a sablon ne kapjon minden változásfigyeléskor új tömböt)
    seenList: { member: ChatMember; seenAt: string | null }[];
    unseenList: { member: ChatMember; seenAt: string | null }[];
    reactionList: { member: ChatMember | null; id: number; emoji: string }[];
  } | null = null;
  lightbox: { images: ChatAttachment[]; index: number } | null = null;
  /** a 3D-ben nézett fájl (STL / 3MF / OBJ) */
  modelView: ChatAttachment | null = null;
  highlight: number | null = null;

  readonly reactions: string[];
  readonly isTouch = window.matchMedia('(hover: none)').matches;

  private memberMap = new Map<number, ChatMember>();
  private subs: Subscription[] = [];
  private typingTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private lastTypingSent = 0;
  private readPending = false;
  private lastMarked = 0;
  private longPressTimer: ReturnType<typeof setTimeout> | null = null;
  private localSeq = 0;
  private dragDepth = 0;
  private viewportHandler = () => this.syncViewport();

  constructor(
    private chat: ChatService,
    private auth: AuthService,
    private router: Router,
    private toast: NotificationService,
    private confirm: ConfirmDialogService,
    private host: ElementRef<HTMLElement>,
    private http: HttpClient,
  ) {
    this.reactions = chat.reactions;
  }

  // ------------------------------------------------------------ életciklus

  ngOnInit(): void {
    this.subs.push(
      this.chat.events$.subscribe((event) => this.onEvent(event)),
      this.chat.online$.subscribe((set) => (this.online = set)),
    );
    document.addEventListener('visibilitychange', this.onVisibility);
    window.visualViewport?.addEventListener('resize', this.viewportHandler);
    window.visualViewport?.addEventListener('scroll', this.viewportHandler);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['members']) {
      this.memberMap = new Map(this.members.map((m) => [m.id, m]));
      this.buildRows();
    }
    if (changes['conversation'] && changes['conversation'].previousValue?.id !== this.conversation?.id) {
      this.resetForConversation();
      this.load();
    }
  }

  ngAfterViewInit(): void {
    this.syncViewport();
  }

  ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.typingTimers.forEach((t) => clearTimeout(t));
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.visualViewport?.removeEventListener('resize', this.viewportHandler);
    window.visualViewport?.removeEventListener('scroll', this.viewportHandler);
    this.pendingFiles.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
    this.chat.setFocus(null);
  }

  private onVisibility = () => this.maybeMarkRead();

  /** Telefonon a billentyűzet a látható területet szűkíti: a nézet ehhez igazodik (iOS is). */
  private syncViewport(): void {
    const vv = window.visualViewport;
    const el = this.host.nativeElement;
    if (!vv || !window.matchMedia('(max-width: 900px)').matches) {
      el.style.removeProperty('--chat-vh');
      el.style.removeProperty('--chat-top');
      return;
    }
    el.style.setProperty('--chat-vh', `${vv.height}px`);
    el.style.setProperty('--chat-top', `${vv.offsetTop}px`);
    if (this.atBottom) {
      this.scrollToBottom();
    }
  }

  private resetForConversation(): void {
    this.messages = [];
    this.rows = [];
    this.reads.clear();
    this.hasMore = false;
    this.newBelow = 0;
    this.typingUsers = [];
    this.replyTo = null;
    this.cancelEdit();
    this.closeMenus();
    this.lastMarked = 0;
    this.text = this.draftFor(this.conversation?.id);
    this.chat.setFocus(this.conversation?.id || null);
  }

  private load(): void {
    if (!this.conversation) {
      return;
    }
    const id = this.conversation.id;
    this.loading = true;
    this.loadError = false;
    this.chat.messages(id).subscribe({
      next: (page) => {
        if (this.conversation?.id !== id) {
          return;
        }
        const locals = this.messages.filter((m) => m.local);
        this.messages = [...page.messages, ...locals];
        this.hasMore = page.hasMore;
        this.reads = new Map(page.reads.map((r) => [r.userId, r]));
        this.loading = false;
        this.buildRows();
        this.scrollToBottom(true);
        this.maybeMarkRead();
        if (!this.isTouch) {
          setTimeout(() => this.input?.nativeElement.focus(), 50);
        }
      },
      error: () => {
        this.loading = false;
        this.loadError = true;
      },
    });
  }

  retryLoad(): void {
    this.load();
  }

  loadOlder(): void {
    const first = this.messages.find((m) => !m.local);
    if (!this.hasMore || this.loadingOlder || !first) {
      return;
    }
    const el = this.scroller?.nativeElement;
    const prevHeight = el?.scrollHeight || 0;
    const prevTop = el?.scrollTop || 0;
    this.loadingOlder = true;
    this.chat.messages(this.conversation.id, first.id).subscribe({
      next: (page) => {
        const known = new Set(this.messages.map((m) => m.id));
        this.messages = [...page.messages.filter((m) => !known.has(m.id)), ...this.messages];
        this.hasMore = page.hasMore;
        this.loadingOlder = false;
        this.buildRows();
        setTimeout(() => {
          if (el) {
            el.scrollTop = el.scrollHeight - prevHeight + prevTop;
          }
        });
      },
      error: () => (this.loadingOlder = false),
    });
  }

  // ------------------------------------------------------------ élő események

  private onEvent(event: ChatStreamEvent): void {
    if (event.type === 'reconnected') {
      this.load();
      return;
    }
    if (event.type === 'presence') {
      return;
    }
    if (!this.conversation || event.data.conversationId !== this.conversation.id) {
      return;
    }
    switch (event.type) {
      case 'message':
        this.receive(event.data);
        break;
      case 'message_update':
        this.replaceMessage(event.data);
        break;
      case 'read': {
        const wasAtBottom = this.isNearBottom();
        this.reads.set(event.data.userId, event.data);
        this.buildRows();
        if (wasAtBottom) {
          this.scrollToBottom();
        }
        break;
      }
      case 'typing':
        this.showTyping(event.data.userId);
        break;
    }
  }

  private receive(message: ChatMessage): void {
    if (this.messages.some((m) => m.id === message.id && !m.local)) {
      return;
    }
    this.stopTyping(message.authorId || 0);
    const mine = message.authorId === this.me;
    if (mine) {
      // a saját, épp küldés alatt álló helyi másolat helyére kerül
      const local = this.messages.find((m) => m.local?.status === 'sending' && m.text === message.text);
      if (local) {
        this.messages = this.messages.map((m) => (m === local ? message : m));
        this.buildRows();
        return;
      }
    }
    const wasAtBottom = this.isNearBottom();
    const firstLocal = this.messages.findIndex((m) => m.local);
    this.messages = firstLocal >= 0
      ? [...this.messages.slice(0, firstLocal), message, ...this.messages.slice(firstLocal)]
      : [...this.messages, message];
    this.buildRows();
    if (wasAtBottom || mine) {
      this.scrollToBottom();
      this.maybeMarkRead();
    } else {
      this.newBelow++;
    }
  }

  private replaceMessage(message: ChatMessage): void {
    const wasAtBottom = this.isNearBottom();
    let changed = false;
    this.messages = this.messages.map((m) => {
      if (m.id === message.id && !m.local) {
        changed = true;
        return message;
      }
      // az idézett üzenet is frissüljön (szerkesztés / törlés)
      if (m.replyTo?.id === message.id) {
        changed = true;
        return { ...m, replyTo: { ...m.replyTo, text: message.text.slice(0, 200), deleted: message.deleted } };
      }
      return m;
    });
    if (changed) {
      this.buildRows();
      if (wasAtBottom) {
        this.scrollToBottom();
      }
    }
    if (this.info?.msg.id === message.id) {
      this.setInfo(message, this.info.seen);
    }
  }

  private showTyping(userId: number): void {
    if (userId === this.me) {
      return;
    }
    clearTimeout(this.typingTimers.get(userId));
    if (!this.typingUsers.includes(userId)) {
      const wasAtBottom = this.isNearBottom();
      this.typingUsers = [...this.typingUsers, userId];
      if (wasAtBottom) {
        this.scrollToBottom();
      }
    }
    this.typingTimers.set(userId, setTimeout(() => this.stopTyping(userId), TYPING_SHOW_MS));
  }

  private stopTyping(userId: number): void {
    clearTimeout(this.typingTimers.get(userId));
    this.typingTimers.delete(userId);
    this.typingUsers = this.typingUsers.filter((id) => id !== userId);
  }

  // ------------------------------------------------------------ megjelenítés

  member(id: number | null | undefined): ChatMember | null {
    return id ? this.memberMap.get(id) || null : null;
  }

  nameOf(id: number | null | undefined): string {
    if (id === this.me) {
      return 'Te';
    }
    return this.member(id)?.name || 'Korábbi tag';
  }

  firstNameOf(id: number | null | undefined): string {
    return id === this.me ? 'Te' : firstName(this.nameOf(id));
  }

  /** „Emese válaszolt Csaba üzenetére" / „Válaszoltál a saját üzenetedre" */
  replyLabel(msg: ViewMessage): string {
    const target = msg.replyTo?.authorId;
    const mine = msg.authorId === this.me;
    const who = mine ? 'Válaszoltál' : `${this.firstNameOf(msg.authorId)} válaszolt`;
    if (target === msg.authorId) {
      return `${who} a saját üzenet${mine ? 'edre' : 'ére'}`;
    }
    if (target === this.me) {
      return `${who} a te üzenetedre`;
    }
    return `${who} ${this.firstNameOf(target)} üzenetére`;
  }

  get peer(): ChatMember | null {
    return this.conversation?.type === 'direct' ? this.member(this.conversation.peerId) : null;
  }

  get title(): string {
    return this.conversation?.type === 'main' ? 'Fő chat' : this.peer?.name || 'Korábbi tag';
  }

  get subtitle(): string {
    if (this.conversation?.type === 'main') {
      const online = this.members.filter((m) => this.online.has(m.id)).length;
      return `${this.members.length} tag · ${online} online`;
    }
    const peer = this.peer;
    if (!peer) {
      return '';
    }
    return this.online.has(peer.id) ? 'Online' : lastSeenLabel(peer.lastSeenAt);
  }

  get peerOnline(): boolean {
    return !!this.peer && this.online.has(this.peer.id);
  }

  get typingLabel(): string {
    const names = this.typingUsers.map((id) => this.firstNameOf(id));
    if (!names.length) {
      return '';
    }
    return names.length === 1 ? `${names[0]} ír…` : `${names.slice(0, -1).join(', ')} és ${names[names.length - 1]} ír…`;
  }

  color(name: string): string {
    return avatarColor(name);
  }

  initials(name: string): string {
    return initials(name);
  }

  clock = clock;
  fullTime = fullTime;
  fileSize = fileSize;
  fileExt = fileExt;

  avatarPath(member: ChatMember | null): string | null {
    return member?.image ? `users/images/${member.image}` : null;
  }

  thumbPath(a: ChatAttachment): string {
    return this.chat.attachmentPath(a, a.hasThumb);
  }

  imageRatio(a: ChatAttachment): string {
    return a.width && a.height ? `${a.width} / ${a.height}` : '4 / 3';
  }

  refIcon(type: string): string {
    return type === 'order' ? 'order' : type === 'ticket' ? 'ticket' : 'mail';
  }

  reactionSummary(msg: ViewMessage): { emojis: string[]; count: number; mine: boolean } {
    const all = msg.reactions || [];
    return {
      emojis: all.slice().sort((a, b) => b.userIds.length - a.userIds.length).slice(0, 3).map((r) => r.emoji),
      count: all.reduce((s, r) => s + r.userIds.length, 0),
      mine: all.some((r) => r.userIds.includes(this.me)),
    };
  }

  myReaction(msg: ViewMessage): string | null {
    return (msg.reactions || []).find((r) => r.userIds.includes(this.me))?.emoji || null;
  }

  canEdit(msg: ViewMessage): boolean {
    return !msg.local && !msg.deleted && msg.authorId === this.me;
  }

  canDelete(msg: ViewMessage): boolean {
    return !msg.local && !msg.deleted && (msg.authorId === this.me || roleNamesOf(this.auth.user).includes('admin'));
  }

  /** A sorok (napelválasztó, időbélyeg, üzenetcsoportok, „látta" fejek) újraszámolása. */
  private buildRows(): void {
    const rows: Row[] = [];
    const list = this.messages;
    // ki meddig látta: a fej az utolsó olyan üzenet alá kerül, amit látott (a saját üzenete alá nem)
    const readersAt = new Map<number, ChatMember[]>();
    this.reads.forEach((state, userId) => {
      if (userId === this.me) {
        return;
      }
      const member = this.memberMap.get(userId);
      if (!member) {
        return;
      }
      let target: ViewMessage | undefined;
      for (let i = list.length - 1; i >= 0; i--) {
        if (!list[i].local && list[i].id <= state.lastReadMessageId) {
          target = list[i];
          break;
        }
      }
      if (target && target.authorId !== userId) {
        readersAt.set(target.id, [...(readersAt.get(target.id) || []), member]);
      }
    });
    const lastMine = [...list].reverse().find((m) => m.authorId === this.me && !m.local && !m.deleted);
    const seenLastMine = lastMine
      ? [...this.reads.values()].some((r) => r.userId !== this.me && r.lastReadMessageId >= lastMine.id)
      : false;

    let prev: ViewMessage | null = null;
    list.forEach((msg, i) => {
      const t = new Date(msg.createdAt).getTime();
      const prevT = prev ? new Date(prev.createdAt).getTime() : 0;
      const newDay = !prev || new Date(prev.createdAt).toDateString() !== new Date(msg.createdAt).toDateString();
      if (newDay) {
        rows.push({ kind: 'day', key: `d${msg.id}${msg.local?.key || ''}`, label: dayLabel(msg.createdAt) });
      } else if (t - prevT > TIME_GAP_MS) {
        rows.push({ kind: 'time', key: `t${msg.id}${msg.local?.key || ''}`, label: clock(msg.createdAt) });
      }
      const next = list[i + 1];
      const sameAsPrev = !!prev && !newDay && prev.authorId === msg.authorId && t - prevT <= GROUP_GAP_MS && !msg.replyTo;
      const sameAsNext = !!next
        && next.authorId === msg.authorId
        && new Date(next.createdAt).getTime() - t <= GROUP_GAP_MS
        && new Date(next.createdAt).toDateString() === new Date(msg.createdAt).toDateString()
        && !next.replyTo;
      const mine = msg.authorId === this.me;
      const attachments = msg.local
        ? msg.local.files.map((f, idx) => ({ id: -idx - 1, name: f.name, mime: f.type, size: f.size, isImage: !!msg.local!.previews[idx], hasThumb: false, width: null, height: null }))
        : msg.attachments;
      rows.push({
        kind: 'msg',
        key: msg.local ? msg.local.key : `m${msg.id}`,
        msg,
        mine,
        first: !sameAsPrev,
        last: !sameAsNext,
        showName: !mine && !sameAsPrev && this.conversation?.type === 'main',
        html: msg.deleted ? '' : linkify(msg.text),
        emojiOnly: !msg.deleted && !attachments.length && !msg.refs.length && !msg.replyTo && isEmojiOnly(msg.text),
        images: attachments.filter((a) => a.isImage),
        files: attachments.filter((a) => !a.isImage),
        readers: msg.local ? [] : readersAt.get(msg.id) || [],
        sent: !!lastMine && msg.id === lastMine.id && !seenLastMine,
      });
      prev = msg;
    });
    this.rows = rows;
  }

  trackRow(_i: number, row: Row): string {
    return row.key;
  }

  localPreview(msg: ViewMessage, a: ChatAttachment): string | null {
    return msg.local ? msg.local.previews[-a.id - 1] : null;
  }

  // ------------------------------------------------------------ görgetés, olvasottság

  onScroll(): void {
    const el = this.scroller?.nativeElement;
    if (!el) {
      return;
    }
    if (el.scrollTop < 120) {
      this.loadOlder();
    }
    this.atBottom = this.isNearBottom();
    if (this.atBottom) {
      this.newBelow = 0;
      this.maybeMarkRead();
    }
    if (this.pickerFor || this.moreFor) {
      this.closeMenus();
    }
  }

  private isNearBottom(): boolean {
    const el = this.scroller?.nativeElement;
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 140;
  }

  scrollToBottom(instant = false): void {
    setTimeout(() => {
      const el = this.scroller?.nativeElement;
      if (el) {
        el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'auto' : 'smooth' });
        this.atBottom = true;
        this.newBelow = 0;
      }
    });
  }

  /** Olvasottnak jelöl, ha a lap látszik és az alján vagyunk. */
  private maybeMarkRead(): void {
    if (document.visibilityState !== 'visible' || this.readPending || !this.conversation || !this.isNearBottom()) {
      return;
    }
    const last = [...this.messages].reverse().find((m) => !m.local);
    const myRead = this.reads.get(this.me)?.lastReadMessageId || 0;
    if (!last || last.id <= Math.max(myRead, this.lastMarked)) {
      return;
    }
    this.readPending = true;
    this.lastMarked = last.id;
    this.chat.markRead(this.conversation.id, last.id).subscribe({
      next: (state) => {
        this.readPending = false;
        this.reads.set(this.me, state);
      },
      error: () => (this.readPending = false),
    });
  }

  // ------------------------------------------------------------ üzenetíró

  private draftKey(id: number | undefined): string {
    return `chat-draft-${this.auth.user?.id}-${id}`;
  }

  private draftFor(id: number | undefined): string {
    try {
      return id ? localStorage.getItem(this.draftKey(id)) || '' : '';
    } catch {
      return '';
    }
  }

  private saveDraft(): void {
    if (this.editing || !this.conversation) {
      return;
    }
    try {
      if (this.text) {
        localStorage.setItem(this.draftKey(this.conversation.id), this.text);
      } else {
        localStorage.removeItem(this.draftKey(this.conversation.id));
      }
    } catch {
      // privát mód: a piszkozat nem kötelező
    }
  }

  onInput(): void {
    this.autoGrow();
    this.saveDraft();
    const now = Date.now();
    if (this.text.trim() && !this.editing && now - this.lastTypingSent > TYPING_SEND_MS) {
      this.lastTypingSent = now;
      this.chat.typing(this.conversation.id);
    }
  }

  autoGrow(): void {
    const el = this.input?.nativeElement;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
      el.style.overflowY = el.scrollHeight > 160 ? 'auto' : 'hidden';
    }
  }

  onKeydown(event: KeyboardEvent): void {
    // asztalon Enter = küldés, Shift+Enter = új sor; telefonon az Enter új sor (a gomb küld)
    if (event.key === 'Enter' && !event.shiftKey && !this.isTouch && !event.isComposing) {
      event.preventDefault();
      this.submit();
    } else if (event.key === 'Escape') {
      if (this.editing) {
        this.cancelEdit();
      } else if (this.replyTo) {
        this.replyTo = null;
      }
    } else if (event.key === 'ArrowUp' && !this.text && !this.editing) {
      // mint a Messengerben / Slackben: felfelé nyíl = az utolsó saját üzenet szerkesztése
      const last = [...this.messages].reverse().find((m) => this.canEdit(m) && !!m.text);
      if (last) {
        event.preventDefault();
        this.startEdit(last);
      }
    }
  }

  get canSend(): boolean {
    return !!this.text.trim() || this.pendingFiles.length > 0 || this.pendingRefs.length > 0;
  }

  submit(): void {
    if (this.editing) {
      this.saveEdit();
      return;
    }
    if (!this.canSend) {
      return;
    }
    const text = this.text.trim();
    const files = this.pendingFiles.map((p) => p.file);
    const previews = this.pendingFiles.map((p) => p.preview);
    const refs = [...this.pendingRefs];
    const replyTo = this.replyTo;
    this.text = '';
    this.pendingFiles = [];
    this.pendingRefs = [];
    this.replyTo = null;
    this.saveDraft();
    setTimeout(() => this.autoGrow());
    this.dispatch({
      key: `l${++this.localSeq}-${Date.now()}`,
      status: 'sending',
      progress: 0,
      files,
      previews,
      refs,
      replyToId: replyTo && !replyTo.local ? replyTo.id : null,
    }, text, replyTo);
  }

  /** Gyors like, ha üres a mező (mint a Messenger 👍 gombja). */
  sendLike(): void {
    this.text = '👍';
    this.submit();
  }

  private dispatch(local: LocalState, text: string, replyTo: ViewMessage | null): void {
    const message: ViewMessage = {
      id: Number.MAX_SAFE_INTEGER - this.localSeq,
      conversationId: this.conversation.id,
      authorId: this.me,
      text,
      createdAt: new Date().toISOString(),
      editedAt: null,
      deleted: false,
      replyTo: replyTo && !replyTo.local
        ? { id: replyTo.id, authorId: replyTo.authorId, text: replyTo.text.slice(0, 200), deleted: replyTo.deleted, attachments: replyTo.attachments.length, refs: replyTo.refs.length }
        : null,
      attachments: [],
      refs: local.refs,
      reactions: [],
      local,
    };
    this.messages = [...this.messages.filter((m) => m.local?.key !== local.key), message];
    this.buildRows();
    this.scrollToBottom();
    const conversationId = this.conversation.id;
    this.chat.send(conversationId, {
      text,
      replyToId: local.replyToId,
      refs: local.refs.map((r) => ({ type: r.type, id: r.id })),
      files: local.files,
    }).subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          this.updateLocal(local.key, { progress: Math.round((event.loaded / event.total) * 100) });
        } else if (event.type === HttpEventType.Response && event.body) {
          const saved = event.body;
          local.previews.forEach((p) => p && URL.revokeObjectURL(p));
          const hasReal = this.messages.some((m) => m.id === saved.id && !m.local);
          this.messages = hasReal
            ? this.messages.filter((m) => m.local?.key !== local.key)
            : this.messages.map((m) => (m.local?.key === local.key ? saved : m));
          this.buildRows();
          this.maybeMarkRead();
        }
      },
      error: (error: HttpErrorResponse) => {
        this.updateLocal(local.key, { status: 'failed' });
        this.toast.show(error?.error?.message || 'Az üzenetet nem sikerült elküldeni.', 'danger');
      },
    });
  }

  private updateLocal(key: string, patch: Partial<LocalState>): void {
    this.messages = this.messages.map((m) => (m.local?.key === key ? { ...m, local: { ...m.local, ...patch } as LocalState } : m));
    this.buildRows();
  }

  retry(msg: ViewMessage): void {
    if (!msg.local) {
      return;
    }
    const replyTo = msg.replyTo ? this.messages.find((m) => m.id === msg.replyTo!.id) || null : null;
    this.dispatch({ ...msg.local, status: 'sending', progress: 0 }, msg.text, replyTo);
  }

  discard(msg: ViewMessage): void {
    msg.local?.previews.forEach((p) => p && URL.revokeObjectURL(p));
    this.messages = this.messages.filter((m) => m !== msg);
    this.buildRows();
  }

  // --- csatolás

  openFilePicker(accept: string): void {
    this.attachMenu = false;
    const input = this.fileInput?.nativeElement;
    if (input) {
      input.accept = accept;
      input.click();
    }
  }

  onFilesChosen(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.addFiles(Array.from(input.files || []));
    input.value = '';
  }

  addFiles(files: File[]): void {
    if (this.editing) {
      this.cancelEdit();
    }
    for (const file of files) {
      if (this.pendingFiles.length >= MAX_FILES) {
        this.toast.show(`Egy üzenethez legfeljebb ${MAX_FILES} fájl csatolható.`, 'warning');
        break;
      }
      if (file.size > MAX_FILE_BYTES) {
        this.toast.show(`„${file.name}" túl nagy (legfeljebb 25 MB).`, 'warning');
        continue;
      }
      const isImage = IMAGE_MIMES.includes(file.type);
      this.pendingFiles = [...this.pendingFiles, { file, preview: isImage ? URL.createObjectURL(file) : null }];
    }
    this.input?.nativeElement.focus();
  }

  removeFile(p: PendingFile): void {
    if (p.preview) {
      URL.revokeObjectURL(p.preview);
    }
    this.pendingFiles = this.pendingFiles.filter((x) => x !== p);
  }

  onPaste(event: ClipboardEvent): void {
    const files = Array.from(event.clipboardData?.files || []);
    if (files.length) {
      event.preventDefault();
      this.addFiles(files);
    }
  }

  onDragEnter(event: DragEvent): void {
    if (event.dataTransfer?.types?.includes('Files')) {
      event.preventDefault();
      this.dragDepth++;
      this.dragOver = true;
    }
  }

  onDragOver(event: DragEvent): void {
    if (this.dragOver) {
      event.preventDefault();
    }
  }

  onDragLeave(): void {
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (!this.dragDepth) {
      this.dragOver = false;
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragDepth = 0;
    this.dragOver = false;
    this.addFiles(Array.from(event.dataTransfer?.files || []));
  }

  openRefPicker(): void {
    this.attachMenu = false;
    this.refPicker = true;
  }

  addRef(card: ChatRefCard): void {
    if (this.editing) {
      this.cancelEdit();
    }
    if (!this.pendingRefs.some((r) => r.type === card.type && r.id === card.id)) {
      if (this.pendingRefs.length >= 5) {
        this.toast.show('Egy üzenethez legfeljebb 5 hivatkozás csatolható.', 'warning');
      } else {
        this.pendingRefs = [...this.pendingRefs, card];
      }
    }
    this.refPicker = false;
    this.input?.nativeElement.focus();
  }

  /** Külső megosztás (pl. ?ref=order:12 a rendelés oldaláról) — a szülő hívja. */
  prefillRef(card: ChatRefCard): void {
    this.addRef(card);
  }

  removeRef(card: ChatRefCard): void {
    this.pendingRefs = this.pendingRefs.filter((r) => r !== card);
  }

  // --- válasz, szerkesztés, törlés, reakció

  startReply(msg: ViewMessage): void {
    this.closeMenus();
    if (this.editing) {
      this.cancelEdit();
    }
    this.replyTo = msg;
    this.input?.nativeElement.focus();
  }

  startEdit(msg: ViewMessage): void {
    this.closeMenus();
    this.replyTo = null;
    this.editing = msg;
    this.text = msg.text;
    setTimeout(() => {
      this.autoGrow();
      const el = this.input?.nativeElement;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  }

  cancelEdit(): void {
    if (this.editing) {
      this.editing = null;
      this.text = this.draftFor(this.conversation?.id);
      setTimeout(() => this.autoGrow());
    }
  }

  private saveEdit(): void {
    const msg = this.editing;
    if (!msg) {
      return;
    }
    const text = this.text.trim();
    if (text === msg.text) {
      this.cancelEdit();
      return;
    }
    this.chat.edit(msg.id, text).subscribe({
      next: (updated) => {
        this.replaceMessage(updated);
        this.editing = null;
        this.text = this.draftFor(this.conversation?.id);
        setTimeout(() => this.autoGrow());
      },
      error: (error: HttpErrorResponse) => this.toast.show(error?.error?.message || 'A módosítás nem sikerült.', 'danger'),
    });
  }

  deleteMessage(msg: ViewMessage): void {
    this.closeMenus();
    this.confirm.open({
      title: 'Üzenet törlése',
      message: 'Mindenkinél törlődik — a helyén „Az üzenetet törölték" felirat marad. A csatolt fájlok is törlődnek.',
      confirmLabel: 'Törlés',
      cancelLabel: 'Mégse',
      tone: 'danger',
    }).subscribe((result) => {
      if (!result?.confirmed) {
        return;
      }
      this.chat.remove(msg.id).subscribe({
        next: (updated) => this.replaceMessage(updated),
        error: (error: HttpErrorResponse) => this.toast.show(error?.error?.message || 'A törlés nem sikerült.', 'danger'),
      });
    });
  }

  react(msg: ViewMessage, emoji: string): void {
    this.closeMenus();
    if (msg.local || msg.deleted) {
      return;
    }
    const next = this.myReaction(msg) === emoji ? null : emoji;
    // azonnali visszajelzés; a szerver válasza / az élő esemény felülírja
    const reactions = msg.reactions
      .map((r) => ({ ...r, userIds: r.userIds.filter((id) => id !== this.me) }))
      .filter((r) => r.userIds.length);
    if (next) {
      const existing = reactions.find((r) => r.emoji === next);
      if (existing) {
        existing.userIds.push(this.me);
      } else {
        reactions.push({ emoji: next, userIds: [this.me] });
      }
    }
    this.replaceMessage({ ...msg, reactions });
    this.chat.react(msg.id, next).subscribe({
      next: (updated) => this.replaceMessage(updated),
      error: () => {
        this.replaceMessage(msg);
        this.toast.show('A reakció nem sikerült.', 'danger');
      },
    });
  }

  copyText(msg: ViewMessage): void {
    this.closeMenus();
    navigator.clipboard?.writeText(msg.text).then(
      () => this.toast.show('Szöveg másolva.', 'success', 1500),
      () => undefined,
    );
  }

  openInfo(msg: ViewMessage): void {
    this.closeMenus();
    this.setInfo(msg, null);
    this.chat.seenBy(msg.id).subscribe({
      next: (list) => {
        if (this.info?.msg.id === msg.id) {
          const seen = list.map((s) => ({ member: this.member(s.userId) || { id: s.userId, name: 'Korábbi tag', image: null, online: false, lastSeenAt: null }, seenAt: s.seenAt }));
          this.setInfo(this.info.msg, seen);
        }
      },
      error: () => this.info && this.setInfo(this.info.msg, []),
    });
  }

  private setInfo(msg: ViewMessage, seen: { member: ChatMember; seenAt: string | null }[] | null): void {
    this.info = {
      msg,
      seen,
      seenList: (seen || []).filter((s) => !!s.seenAt),
      unseenList: (seen || []).filter((s) => !s.seenAt),
      reactionList: (msg.reactions || []).flatMap((r) => r.userIds.map((id) => ({ id, member: this.member(id), emoji: r.emoji }))),
    };
  }

  jumpTo(messageId: number): void {
    const el = this.host.nativeElement.querySelector(`[data-mid="${messageId}"]`) as HTMLElement | null;
    if (!el) {
      this.toast.show('Az idézett üzenet régebbi — görgess feljebb a betöltéséhez.', 'info', 2500);
      return;
    }
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    this.highlight = messageId;
    setTimeout(() => (this.highlight = null), 1600);
  }

  // --- menük (asztal: hover-eszköztár; érintés: hosszú nyomás → alsó lap)

  togglePicker(msg: ViewMessage, event: MouseEvent): void {
    event.stopPropagation();
    const open = this.pickerFor !== msg.id;
    this.closeMenus();
    if (open) {
      this.menuDown = this.nearTop(event);
      this.pickerFor = msg.id;
    }
  }

  toggleMore(msg: ViewMessage, event: MouseEvent): void {
    event.stopPropagation();
    const open = this.moreFor !== msg.id;
    this.closeMenus();
    if (open) {
      this.menuDown = this.nearTop(event);
      this.moreFor = msg.id;
    }
  }

  private nearTop(event: MouseEvent): boolean {
    const top = this.scroller?.nativeElement.getBoundingClientRect().top || 0;
    return (event.target as HTMLElement).getBoundingClientRect().top - top < 220;
  }

  closeMenus(): void {
    this.pickerFor = null;
    this.moreFor = null;
    this.sheet = null;
    this.attachMenu = false;
  }

  onTouchStart(msg: ViewMessage): void {
    if (msg.local) {
      return;
    }
    this.cancelLongPress();
    this.longPressTimer = setTimeout(() => {
      this.longPressTimer = null;
      if (!msg.deleted) {
        navigator.vibrate?.(12);
        this.sheet = msg;
      }
    }, LONG_PRESS_MS);
  }

  cancelLongPress(): void {
    if (this.longPressTimer) {
      clearTimeout(this.longPressTimer);
      this.longPressTimer = null;
    }
  }

  /** Érintésnél egy koppintás megmutatja / elrejti az üzenet idejét. */
  toggleTime(msg: ViewMessage): void {
    this.selectedTimeFor = this.selectedTimeFor === msg.id ? null : msg.id;
  }

  /** Kattintás a szövegben: az ERP-azonosító (T-12, L-5) az appon belül nyílik. */
  onTextClick(event: MouseEvent): void {
    const target = (event.target as HTMLElement).closest('a.chat-idlink') as HTMLAnchorElement | null;
    if (target) {
      event.preventDefault();
      event.stopPropagation();
      this.router.navigateByUrl(target.getAttribute('href') || '/');
    }
  }

  /** a kártya gyors-művelete (beszerzési rendelés jóváhagyása) — csak adminnak látszik, a szerver is ellenőrzi */
  cardBusy: Record<string, boolean> = {};

  get canApprove(): boolean {
    return roleNamesOf(this.auth.user).includes('admin');
  }

  runCardAction(card: ChatRefCard): void {
    const kind = card.action?.kind;
    if (kind !== 'approve_po' && kind !== 'robot_go') return;
    const key = card.type + card.id;
    this.cardBusy[key] = true;
    this.http.post<{ statusLabel: string }>(`purchasing/orders/${card.id}/${kind === 'approve_po' ? 'approve' : 'robot-go'}`, {}).subscribe({
      next: (order) => {
        this.cardBusy[key] = false;
        card.status = order?.statusLabel || 'Jóváhagyva';
        card.action = null;
        this.toast.show(kind === 'approve_po' ? 'Jóváhagyva — Foxi intézi a rendelést. 🦊' : 'Rendben — a robot most leadja a rendelést, pár perc. 🛒', 'success');
      },
      error: (e) => {
        this.cardBusy[key] = false;
        this.toast.show(e?.error?.message || 'Nem sikerült jóváhagyni.', 'danger');
      },
    });
  }

  openRef(card: ChatRefCard): void {
    if (card.link && !card.restricted) {
      this.router.navigateByUrl(card.link);
    }
  }

  // --- képnézegető

  openImage(row: Row, index: number): void {
    if (row.msg?.local) {
      return;
    }
    this.lightbox = { images: row.images || [], index };
  }

  lightboxStep(delta: number): void {
    if (!this.lightbox) {
      return;
    }
    const n = this.lightbox.images.length;
    this.lightbox = { ...this.lightbox, index: (this.lightbox.index + delta + n) % n };
  }

  isModel(a: ChatAttachment): boolean {
    return isModelFile(a.name);
  }

  /** 3D-modell → nézegető; minden más fájl → letöltés. */
  openFile(a: ChatAttachment): void {
    if (this.isModel(a)) {
      this.modelView = a;
    } else {
      this.download(a);
    }
  }

  download(a: ChatAttachment): void {
    this.chat.download(a);
  }

  originalPath(a: ChatAttachment): string {
    return this.chat.attachmentPath(a);
  }

  toggleMute(): void {
    const muted = !this.conversation.muted;
    this.chat.setMuted(this.conversation.id, muted).subscribe({
      next: () => {
        this.mutedChange.emit(muted);
        this.toast.show(muted ? 'Némítva — erről a beszélgetésről nem kapsz push-értesítést.' : 'Az értesítések újra bekapcsolva.', 'info', 2500);
      },
      error: () => this.toast.show('Nem sikerült menteni.', 'danger'),
    });
  }

  @HostListener('document:keydown', ['$event'])
  onDocKey(event: KeyboardEvent): void {
    if (this.modelView && event.key === 'Escape') {
      this.modelView = null;
      return;
    }
    if (this.lightbox) {
      if (event.key === 'Escape') this.lightbox = null;
      if (event.key === 'ArrowRight') this.lightboxStep(1);
      if (event.key === 'ArrowLeft') this.lightboxStep(-1);
      return;
    }
    if (event.key === 'Escape' && (this.pickerFor || this.moreFor || this.sheet || this.info || this.attachMenu)) {
      this.closeMenus();
      this.info = null;
    }
  }

  @HostListener('document:click')
  onDocClick(): void {
    if (this.pickerFor || this.moreFor || this.attachMenu) {
      this.closeMenus();
    }
  }
}
